import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sessionId = (() => {
  let s = localStorage.getItem("av_chat_session");
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("av_chat_session", s);
  }
  return s;
})();

const ChatWidget = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);

  const c = {
    fr: {
      title: "Vibe · Assistant Agadir Vibes",
      sub: "En ligne · réponses instantanées",
      greeting: "Bonjour ! 👋 Je suis Vibe, l'assistant d'Agadir Vibes. Posez-moi vos questions sur nos activités, les prix, la réservation… Je suis là pour vous aider !",
      placeholder: "Écrivez votre message…",
      suggestions: ["Quelles activités proposez-vous ?", "Prix du jet ski ?", "Comment réserver ?"],
      error: "Oups, une erreur est survenue. Réessayez ou contactez-nous sur WhatsApp.",
    },
    en: {
      title: "Vibe · Agadir Vibes Assistant",
      sub: "Online · instant replies",
      greeting: "Hi! 👋 I'm Vibe, the Agadir Vibes assistant. Ask me anything about our activities, prices, booking… I'm here to help!",
      placeholder: "Type your message…",
      suggestions: ["What activities do you offer?", "Jet ski price?", "How do I book?"],
      error: "Oops, something went wrong. Please retry or reach us on WhatsApp.",
    },
  }[lang];

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: c.greeting }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: c.error }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat"
        className="fixed bottom-6 right-6 z-50 w-15 h-15 md:w-16 md:h-16 rounded-full bg-[#7c8f74] text-white flex items-center justify-center shadow-[0_12px_32px_-6px_rgba(124,143,116,0.7)] transition-transform hover:scale-110"
        style={{ width: 60, height: 60 }}
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </button>

      {/* Panel */}
      <div
        className={`fixed bottom-24 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-[380px] transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-90 pointer-events-none"
        }`}
      >
        <div className="bg-[#f7f5ef] rounded-3xl overflow-hidden shadow-[0_30px_70px_-20px_rgba(28,34,30,0.5)] border border-[#e3e0d6] flex flex-col" style={{ height: "min(560px, 72vh)" }}>
          {/* Header */}
          <div className="bg-[#1c221e] px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#7c8f74] flex items-center justify-center shrink-0">
              <MessageCircle size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{c.title}</p>
              <p className="text-[#8fb37f] text-[11px] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8fb37f] inline-block" /> {c.sub}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#7c8f74] text-white rounded-br-md"
                      : "bg-white text-[#2b332d] border border-[#eceae1] rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#eceae1] rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="w-2 h-2 rounded-full bg-[#b7c1ad] animate-bounce"
                      style={{ animationDelay: `${d * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-2">
                {c.suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    className="text-xs bg-white border border-[#dedbd0] text-[#5c6b54] px-3 py-1.5 rounded-full hover:border-[#7c8f74] transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 bg-[#f7f5ef] border-t border-[#e6e3d9] flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={c.placeholder}
              className="flex-1 bg-white border border-[#dedbd0] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#7c8f74]"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#7c8f74] hover:bg-[#697c62] disabled:opacity-40 text-white flex items-center justify-center transition shrink-0"
            >
              <Send size={17} />
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
